/**
 * OperatorIntelligence Component
 * ================================
 * Intelligence tab for Operator agents.
 * 
 * Sections:
 * A. Summary Dashboard - Stats overview
 * B. Insights - Read-only analytics patterns
 * C. Proposals - Approve/decline improvement suggestions
 * D. Guidance - Manual intelligence notes
 * E. ClockCounterClockwise - Version history with rollback
 * 
 * Key Principles:
 * - ALL changes require explicit approval
 * - NEVER auto-mutates behaviour
 * - Full audit trail
 */
import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { BACKEND_URL } from '../config';
import { useOrganization } from '../context/OrganizationContext';
import { fetchWithOrg } from '../utils/api';
import { toast } from 'sonner';
import OperatorIdentity from './OperatorIdentity';

import {
  ArrowCounterClockwise,
  ArrowsClockwise,
  Brain,
  CaretDown,
  CaretRight,
  CheckCircle,
  Clock,
  ClockCounterClockwise,
  Eye,
  FileText,
  FloppyDisk,
  GearFine,
  Lightning,
  Pulse,
  Shield,
  Target,
  TrendUp,
  User,
  Warning,
  XCircle
} from '@phosphor-icons/react';
const OperatorIntelligence = ({ operatorId }) => {
  const { activeOrganization } = useOrganization();
  
  // State
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [insights, setInsights] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [guidance, setGuidance] = useState(null);
  const [guidanceHistory, setGuidanceHistory] = useState([]);
  const [history, setHistory] = useState([]);
  const [orgSettings, setOrgSettings] = useState(null);
  
  // UI state
  const [activeSection, setActiveSection] = useState('summary');
  const [guidanceText, setGuidanceText] = useState('');
  const [savingGuidance, setSavingGuidance] = useState(false);
  const [analyzingAgent, setAnalyzingAgent] = useState(false);
  const [expandedInsight, setExpandedInsight] = useState(null);
  const [expandedProposal, setExpandedProposal] = useState(null);
  const [declineReason, setDeclineReason] = useState('');
  const [showDeclineModal, setShowDeclineModal] = useState(null);
  const [showRollbackModal, setShowRollbackModal] = useState(null);
  const [rollbackReason, setRollbackReason] = useState('');

  // Fetch all data
  const fetchData = useCallback(async () => {
    if (!activeOrganization || !operatorId) return;
    
    setLoading(true);
    try {
      const orgId = activeOrganization.organization_id;
      
      // Fetch summary
      const summaryRes = await fetchWithOrg(
        `${BACKEND_URL}/api/exec/intelligence/${operatorId}/summary`,
        {}, orgId
      );
      if (summaryRes.ok) {
        setSummary(await summaryRes.json());
      }
      
      // Fetch insights
      const insightsRes = await fetchWithOrg(
        `${BACKEND_URL}/api/exec/intelligence/${operatorId}/insights`,
        {}, orgId
      );
      if (insightsRes.ok) {
        const data = await insightsRes.json();
        setInsights(data.insights || []);
      }
      
      // Fetch proposals
      const proposalsRes = await fetchWithOrg(
        `${BACKEND_URL}/api/exec/intelligence/${operatorId}/proposals`,
        {}, orgId
      );
      if (proposalsRes.ok) {
        const data = await proposalsRes.json();
        setProposals(data.proposals || []);
      }
      
      // Fetch guidance
      const guidanceRes = await fetchWithOrg(
        `${BACKEND_URL}/api/exec/intelligence/${operatorId}/guidance`,
        {}, orgId
      );
      if (guidanceRes.ok) {
        const data = await guidanceRes.json();
        setGuidance(data.guidance);
        setGuidanceText(data.guidance?.guidance_text || '');
      }
      
      // Fetch guidance history
      const historyRes = await fetchWithOrg(
        `${BACKEND_URL}/api/exec/intelligence/${operatorId}/guidance/history`,
        {}, orgId
      );
      if (historyRes.ok) {
        const data = await historyRes.json();
        setGuidanceHistory(data.history || []);
      }
      
      // Fetch full history
      const fullHistoryRes = await fetchWithOrg(
        `${BACKEND_URL}/api/exec/intelligence/${operatorId}/history`,
        {}, orgId
      );
      if (fullHistoryRes.ok) {
        const data = await fullHistoryRes.json();
        setHistory(data.history || []);
      }
      
      // Fetch org settings
      const settingsRes = await fetchWithOrg(
        `${BACKEND_URL}/api/exec/intelligence/org/settings`,
        {}, orgId
      );
      if (settingsRes.ok) {
        setOrgSettings(await settingsRes.json());
      }
      
    } catch (error) {
      console.error('Error fetching intelligence data:', error);
    } finally {
      setLoading(false);
    }
  }, [activeOrganization, operatorId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Trigger analysis via scheduler endpoint
  const handleAnalyze = async () => {
    if (!activeOrganization) return;
    
    // Safety check - don't allow analysis if no execution data
    const hasExecutionData = summary?.total_runs > 0;
    if (!hasExecutionData) {
      return;
    }
    
    setAnalyzingAgent(true);
    try {
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/exec/intelligence/${operatorId}/scheduler/run`,
        { method: 'POST' },
        activeOrganization.organization_id
      );
      
      if (response.ok) {
        const data = await response.json();
        
        if (data.status === 'skipped_throttle') {
          toast.info(`Analysis skipped - last run was less than ${data.min_interval_minutes} minutes ago`);
        } else if (data.status === 'completed') {
          toast.success(`Analysis complete: ${data.insights_created || 0} insights, ${data.proposals_generated || 0} proposals`);
          fetchData();
        } else if (data.status === 'failed') {
          toast.error(`Analysis failed: ${data.error || 'Unknown error'}`);
        }
      } else {
        toast.error('Analysis failed');
      }
    } catch (error) {
      toast.error('Analysis failed');
    } finally {
      setAnalyzingAgent(false);
    }
  };

  // Approve proposal
  const handleApprove = async (proposalId) => {
    if (!activeOrganization) return;
    
    try {
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/exec/intelligence/${operatorId}/proposals/${proposalId}/approve`,
        { method: 'POST' },
        activeOrganization.organization_id
      );
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`Approved! Version: ${data.version_label}`);
        if (data.propagated_to?.length > 0) {
          toast.info(`Propagated to ${data.propagated_to.length} other agents`);
        }
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Approval failed');
      }
    } catch (error) {
      toast.error('Approval failed');
    }
  };

  // Decline proposal
  const handleDecline = async (proposalId) => {
    if (!activeOrganization) return;
    
    try {
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/exec/intelligence/${operatorId}/proposals/${proposalId}/decline`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ reason: declineReason })
        },
        activeOrganization.organization_id
      );
      
      if (response.ok) {
        toast.success('Proposal declined');
        setShowDeclineModal(null);
        setDeclineReason('');
        fetchData();
      } else {
        toast.error('Failed to decline');
      }
    } catch (error) {
      toast.error('Failed to decline');
    }
  };

  // Rollback changeset
  const handleRollback = async (changesetId) => {
    if (!activeOrganization) return;
    
    try {
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/exec/intelligence/${operatorId}/changesets/${changesetId}/rollback`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ reason: rollbackReason })
        },
        activeOrganization.organization_id
      );
      
      if (response.ok) {
        toast.success('Changeset rolled back');
        setShowRollbackModal(null);
        setRollbackReason('');
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Rollback failed');
      }
    } catch (error) {
      toast.error('Rollback failed');
    }
  };

  // FloppyDisk guidance
  const handleSaveGuidance = async () => {
    if (!activeOrganization || !guidanceText.trim()) return;
    
    setSavingGuidance(true);
    try {
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/exec/intelligence/${operatorId}/guidance`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ guidance_text: guidanceText })
        },
        activeOrganization.organization_id
      );
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`Guidance saved (v${data.version})`);
        fetchData();
      } else {
        toast.error('Failed to save guidance');
      }
    } catch (error) {
      toast.error('Failed to save guidance');
    } finally {
      setSavingGuidance(false);
    }
  };

  // Helper components
  const StatCard = ({ icon: Icon, label, value, subtext, color = 'blue' }) => (
    <div className="glass rounded-xl p-4 border border-[rgba(255,255,255,0.1)]">
      <div className="flex items-start justify-between">
        <div className={`p-2 rounded-lg bg-${color}-500/20`}>
          <Icon size={20} className={`text-${color}-400`} />
        </div>
        <span className="text-2xl font-bold text-white">{value}</span>
      </div>
      <p className="text-sm text-[#B0B8C4] mt-2">{label}</p>
      {subtext && <p className="text-xs text-[#7D8896] mt-1">{subtext}</p>}
    </div>
  );

  const ImpactBadge = ({ level }) => {
    const colors = {
      low: 'bg-green-500/20 text-[#00E5C0]',
      medium: 'bg-yellow-500/20 text-yellow-400',
      high: 'bg-red-500/20 text-[#FF6B6B]'
    };
    return (
      <span className={`px-2 py-0.5 text-xs rounded ${colors[level] || colors.low}`}>
        {level}
      </span>
    );
  };

  const StatusBadge = ({ status }) => {
    const styles = {
      pending: 'bg-yellow-500/20 text-yellow-400',
      approved: 'bg-green-500/20 text-[#00E5C0]',
      declined: 'bg-red-500/20 text-[#FF6B6B]',
      rolled_back: 'bg-orange-500/20 text-orange-400',
      active: 'bg-blue-500/20 text-blue-400'
    };
    return (
      <span className={`px-2 py-0.5 text-xs rounded ${styles[status] || styles.pending}`}>
        {status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <ArrowsClockwise className="w-6 h-6 text-[#00E5C0] animate-spin" />
        <span className="ml-2 text-[#B0B8C4]">Loading intelligence data...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-gradient-to-br from-[#00E5C0]/20 to-pink-500/20">
            <Brain size={24} className="text-[#00E5C0]" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-white">Intelligence</h2>
            <p className="text-sm text-[#B0B8C4]">Governed insights and improvements</p>
          </div>
        </div>
        {/* Analyze Now Button with Smart Disabled State */}
        {(() => {
          const hasExecutionData = summary?.total_runs > 0;
          const isDisabled = analyzingAgent || !hasExecutionData;
          
          return (
            <div className="relative group">
              <button
                onClick={hasExecutionData ? handleAnalyze : undefined}
                disabled={isDisabled}
                className={`px-4 py-2 bg-gradient-to-r from-[#00E5C0] to-pink-500 text-white rounded-lg flex items-center gap-2 transition-all ${
                  hasExecutionData 
                    ? 'hover:opacity-90 cursor-pointer' 
                    : 'opacity-50 cursor-not-allowed'
                } ${analyzingAgent ? 'opacity-50' : ''}`}
                style={!hasExecutionData ? { pointerEvents: 'auto' } : {}}
              >
                {analyzingAgent ? (
                  <ArrowsClockwise size={16} className="animate-spin" />
                ) : (
                  <Lightning size={16} />
                )}
                Analyze Now
              </button>
              {/* Tooltip for disabled state - positioned to the left to avoid right edge cutoff */}
              {!hasExecutionData && !loading && (
                <div className="absolute bottom-full right-0 mb-2 px-3 py-2 bg-[#1a1a2e] border border-[rgba(255,255,255,0.1)] rounded-lg text-sm text-[#F0F4F8] whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50 shadow-lg">
                  <div className="flex items-center gap-2">
                    <Warning size={14} className="text-amber-400" />
                    No execution data yet. Run this agent before analyzing.
                  </div>
                  {/* Tooltip arrow */}
                  <div className="absolute top-full right-4 -mt-px border-4 border-transparent border-t-[#1a1a2e]" />
                </div>
              )}
            </div>
          );
        })()}
      </div>

      {/* Section Tabs */}
      <div className="flex gap-2 flex-wrap border-b border-[rgba(255,255,255,0.1)] pb-2">
        {[
          { id: 'summary', label: 'Summary', icon: Pulse },
          { id: 'insights', label: 'Insights', icon: Eye, count: insights.length },
          { id: 'proposals', label: 'Proposals', icon: Target, count: proposals.filter(p => p.status === 'pending').length },
          { id: 'guidance', label: 'Guidance', icon: FileText },
          { id: 'identity', label: 'Identity', icon: User },
          { id: 'history', label: 'History', icon: ClockCounterClockwise },
        ].map(section => (
          <button
            key={section.id}
            onClick={() => setActiveSection(section.id)}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all ${
              activeSection === section.id
                ? 'bg-[#00E5C0]/20 text-[#00E5C0]'
                : 'text-[#B0B8C4] hover:text-white hover:bg-[rgba(255,255,255,0.05)]'
            }`}
          >
            <section.icon size={16} />
            {section.label}
            {section.count > 0 && (
              <span className="px-1.5 py-0.5 text-xs bg-[#00E5C0]/30 rounded">
                {section.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Section Content */}
      <AnimatePresence mode="wait">
        {/* Summary Section */}
        {activeSection === 'summary' && summary && (
          <motion.div
            key="summary"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="space-y-6"
          >
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard
                icon={Pulse}
                label="Total Runs (7d)"
                value={summary.total_runs}
                color="blue"
              />
              <StatCard
                icon={Warning}
                label="Fallback Rate"
                value={`${(summary.fallback_rate * 100).toFixed(1)}%`}
                subtext={summary.fallback_rate > 0.15 ? 'Above threshold' : 'Normal'}
                color={summary.fallback_rate > 0.15 ? 'yellow' : 'green'}
              />
              <StatCard
                icon={Clock}
                label="Avg Response"
                value={`${summary.avg_execution_time_ms.toFixed(0)}ms`}
                color="purple"
              />
              <StatCard
                icon={Brain}
                label="Active Insights"
                value={summary.active_insights_count}
                color="pink"
              />
            </div>
            
            <div className="glass rounded-xl p-6 border border-[rgba(255,255,255,0.1)]">
              <h3 className="text-lg font-medium text-white mb-4">Current State</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-[#B0B8C4]">Intelligence Version:</span>
                  <span className="ml-2 text-white font-medium">{summary.current_version}</span>
                </div>
                <div>
                  <span className="text-[#B0B8C4]">Pending Proposals:</span>
                  <span className="ml-2 text-yellow-400 font-medium">{summary.pending_proposals_count}</span>
                </div>
                <div>
                  <span className="text-[#B0B8C4]">Approved Changes:</span>
                  <span className="ml-2 text-[#00E5C0] font-medium">{summary.approved_changesets_count}</span>
                </div>
                <div>
                  <span className="text-[#B0B8C4]">Guidance:</span>
                  <span className="ml-2 text-white">
                    {summary.has_guidance ? `v${summary.guidance_version}` : 'Not set'}
                  </span>
                </div>
              </div>
            </div>

            {/* Org Settings */}
            {orgSettings && (
              <div className="glass rounded-xl p-4 border border-[rgba(255,255,255,0.1)]">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <GearFine size={16} className="text-[#B0B8C4]" />
                    <span className="text-sm text-[#B0B8C4]">Org Intelligence Sharing:</span>
                  </div>
                  <span className={`text-sm ${orgSettings.share_intelligence_enabled ? 'text-[#00E5C0]' : 'text-[#7D8896]'}`}>
                    {orgSettings.share_intelligence_enabled ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
              </div>
            )}
          </motion.div>
        )}

        {/* Insights Section */}
        {activeSection === 'insights' && (
          <motion.div
            key="insights"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="space-y-4"
          >
            {insights.length === 0 ? (
              <div className="text-center py-12 text-[#B0B8C4]">
                <Eye size={48} className="mx-auto mb-4 opacity-50" />
                <p>No active insights</p>
                <p className="text-sm mt-2">Run analysis to detect patterns</p>
              </div>
            ) : (
              insights.map(insight => (
                <div
                  key={insight.id}
                  className="glass rounded-xl border border-[rgba(255,255,255,0.1)] overflow-hidden"
                >
                  <div
                    className="p-4 cursor-pointer flex items-center justify-between"
                    onClick={() => setExpandedInsight(expandedInsight === insight.id ? null : insight.id)}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${
                        insight.impact_score > 0.7 ? 'bg-red-500/20' :
                        insight.impact_score > 0.4 ? 'bg-yellow-500/20' : 'bg-blue-500/20'
                      }`}>
                        <Warning size={18} className={
                          insight.impact_score > 0.7 ? 'text-[#FF6B6B]' :
                          insight.impact_score > 0.4 ? 'text-yellow-400' : 'text-blue-400'
                        } />
                      </div>
                      <div>
                        <p className="text-white font-medium">{insight.summary}</p>
                        <p className="text-sm text-[#B0B8C4]">
                          {insight.insight_type.replace(/_/g, ' ')} • {insight.frequency} occurrences
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-[#B0B8C4]">
                        Impact: {(insight.impact_score * 100).toFixed(0)}%
                      </span>
                      {expandedInsight === insight.id ? <CaretDown size={18} /> : <CaretRight size={18} />}
                    </div>
                  </div>
                  
                  {expandedInsight === insight.id && (
                    <div className="p-4 border-t border-[rgba(255,255,255,0.1)] bg-[rgba(0,0,0,0.2)]">
                      <pre className="text-xs text-[#B0B8C4] overflow-auto">
                        {JSON.stringify(insight.supporting_metrics, null, 2)}
                      </pre>
                    </div>
                  )}
                </div>
              ))
            )}
          </motion.div>
        )}

        {/* Proposals Section */}
        {activeSection === 'proposals' && (
          <motion.div
            key="proposals"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="space-y-4"
          >
            {proposals.filter(p => p.status === 'pending').length === 0 ? (
              <div className="text-center py-12 text-[#B0B8C4]">
                <Target size={48} className="mx-auto mb-4 opacity-50" />
                <p>No pending proposals</p>
                <p className="text-sm mt-2">Run analysis to generate improvement suggestions</p>
              </div>
            ) : (
              proposals.filter(p => p.status === 'pending').map(proposal => (
                <div
                  key={proposal.id}
                  className="glass rounded-xl border border-[rgba(255,255,255,0.1)] overflow-hidden"
                >
                  <div className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h4 className="text-white font-medium">{proposal.title}</h4>
                        <p className="text-sm text-[#B0B8C4] mt-1">
                          {proposal.proposal_type.replace(/_/g, ' ')}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <ImpactBadge level={proposal.impact_level} />
                        <span className="px-2 py-0.5 text-xs bg-blue-500/20 text-blue-400 rounded">
                          Risk: {proposal.risk_level}
                        </span>
                      </div>
                    </div>
                    
                    <p className="text-sm text-[#F0F4F8] whitespace-pre-line mb-4">
                      {proposal.description}
                    </p>
                    
                    <div
                      className="cursor-pointer"
                      onClick={() => setExpandedProposal(expandedProposal === proposal.id ? null : proposal.id)}
                    >
                      <div className="flex items-center gap-2 text-sm text-[#B0B8C4] mb-2">
                        <span>Proposed Change</span>
                        {expandedProposal === proposal.id ? <CaretDown size={14} /> : <CaretRight size={14} />}
                      </div>
                      {expandedProposal === proposal.id && (
                        <pre className="text-xs text-[#B0B8C4] bg-[rgba(0,0,0,0.3)] p-3 rounded overflow-auto mb-4">
                          {JSON.stringify(proposal.proposed_change, null, 2)}
                        </pre>
                      )}
                    </div>
                    
                    <div className="flex gap-3 pt-3 border-t border-[rgba(255,255,255,0.1)]">
                      <button
                        onClick={() => handleApprove(proposal.id)}
                        className="flex-1 px-4 py-2 bg-green-500/20 text-[#00E5C0] rounded-lg flex items-center justify-center gap-2 hover:bg-green-500/30 transition-colors"
                      >
                        <CheckCircle size={16} />
                        Approve
                      </button>
                      <button
                        onClick={() => setShowDeclineModal(proposal.id)}
                        className="flex-1 px-4 py-2 bg-red-500/20 text-[#FF6B6B] rounded-lg flex items-center justify-center gap-2 hover:bg-red-500/30 transition-colors"
                      >
                        <XCircle size={16} />
                        Decline
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </motion.div>
        )}

        {/* Guidance Section */}
        {activeSection === 'guidance' && (
          <motion.div
            key="guidance"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="space-y-4"
          >
            <div className="glass rounded-xl p-6 border border-[rgba(255,255,255,0.1)]">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-white">Agent Guidance</h3>
                {guidance && (
                  <span className="text-sm text-[#B0B8C4]">
                    Version {guidance.version_number}
                  </span>
                )}
              </div>
              
              <p className="text-sm text-[#B0B8C4] mb-4">
                Optional constraints and preferences for this agent. Changes are versioned.
              </p>
              
              <textarea
                value={guidanceText}
                onChange={(e) => setGuidanceText(e.target.value)}
                placeholder="Enter guidance for this agent (e.g., 'Always confirm before executing file operations', 'Prefer browser tool over direct API calls')"
                className="w-full h-40 px-4 py-3 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-pink-500 resize-none"
              />
              
              <div className="flex justify-end mt-4">
                <button
                  onClick={handleSaveGuidance}
                  disabled={savingGuidance || !guidanceText.trim()}
                  className="px-4 py-2 bg-gradient-to-r from-[#00E5C0] to-pink-500 text-white rounded-lg flex items-center gap-2 hover:opacity-90 disabled:opacity-50"
                >
                  {savingGuidance ? (
                    <ArrowsClockwise size={16} className="animate-spin" />
                  ) : (
                    <FloppyDisk size={16} />
                  )}
                  Save Guidance
                </button>
              </div>
            </div>
            
            {/* Guidance ClockCounterClockwise */}
            {guidanceHistory.length > 1 && (
              <div className="glass rounded-xl p-4 border border-[rgba(255,255,255,0.1)]">
                <h4 className="text-sm font-medium text-[#B0B8C4] mb-3">Version ClockCounterClockwise</h4>
                <div className="space-y-2">
                  {guidanceHistory.slice(0, 5).map((g, i) => (
                    <div
                      key={g.id}
                      className={`p-3 rounded-lg text-sm ${
                        i === 0 ? 'bg-[#00E5C0]/10 border border-pink-500/20' : 'bg-[rgba(255,255,255,0.02)]'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-white">v{g.version_number}</span>
                        <span className="text-[#7D8896] text-xs">
                          {new Date(g.created_at).toLocaleDateString()}
                        </span>
                      </div>
                      <p className="text-[#B0B8C4] text-xs mt-1 truncate">
                        {g.guidance_text.substring(0, 100)}...
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        )}

        {/* Identity Section */}
        {activeSection === 'identity' && (
          <motion.div
            key="identity"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
          >
            <OperatorIdentity 
              operatorId={operatorId} 
              onUpdate={fetchData}
            />
          </motion.div>
        )}

        {/* ClockCounterClockwise Section */}
        {activeSection === 'history' && (
          <motion.div
            key="history"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="space-y-4"
          >
            {history.length === 0 ? (
              <div className="text-center py-12 text-[#B0B8C4]">
                <ClockCounterClockwise size={48} className="mx-auto mb-4 opacity-50" />
                <p>No history yet</p>
              </div>
            ) : (
              history.map((item, index) => (
                <div
                  key={item.data.id || index}
                  className="glass rounded-xl p-4 border border-[rgba(255,255,255,0.1)]"
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <StatusBadge status={item.status} />
                        <span className="text-sm text-[#B0B8C4]">{item.type}</span>
                      </div>
                      <p className="text-white font-medium">
                        {item.data.version_label || item.data.title || 'Change'}
                      </p>
                      <p className="text-sm text-[#7D8896] mt-1">
                        {new Date(item.date).toLocaleString()}
                      </p>
                    </div>
                    
                    {item.type === 'changeset' && item.status === 'approved' && item.data.rollback_available && (
                      <button
                        onClick={() => setShowRollbackModal(item.data.id)}
                        className="px-3 py-1 text-xs bg-orange-500/20 text-orange-400 rounded hover:bg-orange-500/30 flex items-center gap-1"
                      >
                        <ArrowCounterClockwise size={12} />
                        Rollback
                      </button>
                    )}
                  </div>
                  
                  {item.data.decline_reason && (
                    <p className="text-sm text-[#FF6B6B] mt-2">
                      Reason: {item.data.decline_reason}
                    </p>
                  )}
                  
                  {item.data.rollback_reason && (
                    <p className="text-sm text-orange-400 mt-2">
                      Rollback reason: {item.data.rollback_reason}
                    </p>
                  )}
                </div>
              ))
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Decline Modal */}
      {showDeclineModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="glass rounded-xl border border-[rgba(255,255,255,0.1)] w-full max-w-md">
            <div className="p-6">
              <h3 className="text-lg font-medium text-white mb-4">Decline Proposal</h3>
              <textarea
                value={declineReason}
                onChange={(e) => setDeclineReason(e.target.value)}
                placeholder="Reason for declining (optional)"
                className="w-full h-24 px-4 py-3 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-red-500 resize-none"
              />
              <div className="flex gap-3 mt-4">
                <button
                  onClick={() => { setShowDeclineModal(null); setDeclineReason(''); }}
                  className="flex-1 px-4 py-2 glass rounded-lg text-[#B0B8C4] hover:text-white"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDecline(showDeclineModal)}
                  className="flex-1 px-4 py-2 bg-red-500/20 text-[#FF6B6B] rounded-lg hover:bg-red-500/30"
                >
                  Confirm Decline
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Rollback Modal */}
      {showRollbackModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="glass rounded-xl border border-[rgba(255,255,255,0.1)] w-full max-w-md">
            <div className="p-6">
              <h3 className="text-lg font-medium text-white mb-4">Rollback Changeset</h3>
              <p className="text-sm text-[#B0B8C4] mb-4">
                This will restore the previous configuration. This action is reversible.
              </p>
              <textarea
                value={rollbackReason}
                onChange={(e) => setRollbackReason(e.target.value)}
                placeholder="Reason for rollback (optional)"
                className="w-full h-24 px-4 py-3 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-orange-500 resize-none"
              />
              <div className="flex gap-3 mt-4">
                <button
                  onClick={() => { setShowRollbackModal(null); setRollbackReason(''); }}
                  className="flex-1 px-4 py-2 glass rounded-lg text-[#B0B8C4] hover:text-white"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleRollback(showRollbackModal)}
                  className="flex-1 px-4 py-2 bg-orange-500/20 text-orange-400 rounded-lg hover:bg-orange-500/30"
                >
                  Confirm Rollback
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OperatorIntelligence;
